import { useEffect, useState } from "react";
import { FileText, MessageSquare, Trash2 } from "lucide-react";
import { ChatSession } from "@/types/chat";
import { chatApi } from "@/lib/api/chat";

interface ChatSessionsListProps {
  licitacionId?: string;
  onSessionSelect?: (sessionId: string) => void;
  onNewChat?: () => void;
  selectedSessionId?: string;
  onSessionUpdate?: () => void;
  showNewButton?: boolean;
  // Mejora "Asistente con ámbito": mapa id → título para etiquetar cada
  // sesión con la licitación asociada (antes solo se veía el título de
  // la conversación, sin contexto de a qué licitación pertenecía).
  licitacionesById?: Record<string, string>;
}

export default function ChatSessionsList({
  licitacionId,
  onSessionSelect,
  onNewChat,
  selectedSessionId,
  showNewButton = true,
  licitacionesById,
}: ChatSessionsListProps) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = async (forLicitacionId?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatApi.listSessions(forLicitacionId);
      setSessions(response.sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar sesiones");
      console.error("Error al cargar sesiones:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSessions(licitacionId);
  }, [licitacionId]);

  const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("¿Eliminar esta conversación?")) return;
    try {
      await chatApi.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      console.error("Error al eliminar sesión:", err);
      alert("Error al eliminar la sesión");
    }
  };

  const handleNewSession = async () => {
    try {
      const newSession = await chatApi.createSession({
        licitacion_id: licitacionId,
        titulo: "Nueva conversación",
      });
      setSessions((prev) => [newSession, ...prev]);
      onSessionSelect?.(newSession.id);
    } catch (err) {
      console.error("Error al crear sesión:", err);
      alert("Error al crear nueva sesión");
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-sm text-content-muted">
        Cargando conversaciones…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-sm text-danger">
        <p>{error}</p>
        <button
          onClick={() => loadSessions(licitacionId)}
          className="mt-2 text-brand hover:text-brand-hover font-heading font-semibold"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-line rounded shadow-card h-full flex flex-col overflow-hidden">
      {showNewButton && (
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h3 className="font-heading font-semibold text-sm text-ink">
            Conversaciones
          </h3>
          <button
            onClick={onNewChat || handleNewSession}
            className="px-2.5 py-1 bg-brand text-white text-xs rounded font-heading font-semibold hover:bg-brand-hover transition-colors"
          >
            + Nueva
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="p-6 text-center text-sm text-content-muted">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-line-faint" />
            <p className="font-heading font-semibold text-content">
              No hay conversaciones
            </p>
            <p className="text-xs mt-1">Crea una nueva para empezar.</p>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {sessions.map((session) => {
              const licitacionTitulo =
                session.licitacion_id && licitacionesById?.[session.licitacion_id];
              const active = selectedSessionId === session.id;
              return (
                <li
                  key={session.id}
                  onClick={() => onSessionSelect?.(session.id)}
                  className={`group px-4 py-3 cursor-pointer hover:bg-surface-100 transition-colors border-l-2 ${active ? "bg-brand/8 border-brand" : "border-transparent"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm truncate ${active ? "font-heading font-semibold text-ink" : "text-content"}`}
                      >
                        {session.titulo || "Sin título"}
                      </p>
                      {licitacionTitulo && (
                        <p className="flex items-center gap-1 text-[11px] text-brand-hover mt-0.5 truncate">
                          <FileText className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{licitacionTitulo}</span>
                        </p>
                      )}
                      <p className="text-[11px] text-content-muted mt-0.5">
                        {session.message_count}{" "}
                        {session.message_count === 1 ? "mensaje" : "mensajes"}
                        {" · "}
                        {new Date(session.updated_at).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteSession(session.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-content-muted hover:text-danger transition-all flex-shrink-0"
                      aria-label={`Eliminar conversación ${session.titulo || "Sin título"}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
